import { LEASE } from '@makemydeal/dr-platform-types';
import type { AnyDispatch } from '@makemydeal/dr-browser-utils/typedefs/types/reduxTypes';

import { CALC_ENDPOINTS } from '../constants';
import {
    getInitialDownPaymentListForGrid,
    getInitialTermsMonthsListForGrid,
    getPaymentGridCashDown,
    getPaymentGridItems,
    getPaymentGridTerms
} from '../selectors/paymentGridSelectors';
import { getLeaseRecordsByTermAndDownPayment, getOffer, leaseRecordKey } from '../selectors/offerNodeSelectors';
import {
    initializePaymentGridCashdown,
    initializePaymentGridTerms,
    loadPaymentGridLenderPrograms,
    loadPaymentGridTermLenderProgram,
    updatePaymentGridRow,
    updateCurrentCalculationEndpoint
} from './creators';
import { addPaymentGridMeta, tagOfferType } from '../utils/paymentGridUtils';

const toNumbers = (values: (number | null)[]): number[] => values.filter((v): v is number => v != null);

/** True when the lease grid already has computed cells or a row in flight — i.e. genuinely populated
 *  (as opposed to carrying axes only, which a saved deal can have without hydrated payments). */
const hasLeaseCellDataOrLoading = (state: any): boolean => {
    const rows = getPaymentGridItems(state, LEASE) as Array<{ items: Array<{ isEmpty: boolean; isLoading?: boolean }> }>;
    return rows.some((row) => row.items.some((cell) => !cell.isEmpty || cell.isLoading));
};

/**
 * True when cached lease term records (each carrying a computed payment) cover every down-payment row
 * of the given axes — i.e. the grid view can be rehydrated from Redux without new PS calls.
 *
 * RESET_PAYMENT_GRID (the main lease grid's toggle-off) clears `paymentGridData.terms/cashDown` (the
 * grid view) but leaves the term records in `offer.terms`. Coverage is checked against the slideout's
 * canonical axes — not a union of every persisted record — so records from another grid context (e.g.
 * the main grid's own down payments) don't add extra rows to the slideout's 3x3. Lookup is keyed on
 * requestedDownPayment via getLeaseRecordsByTermAndDownPayment (the raw axis value, stable even when
 * the stored term id was re-keyed to a floored cash-down).
 */
const leaseRecordsCoverRows = (state: any, termMonthsList: number[], downPaymentList: number[]): boolean => {
    const recordsByKey = getLeaseRecordsByTermAndDownPayment(getOffer(state));
    return downPaymentList.every((downPayment) =>
        termMonthsList.some((months) => recordsByKey.get(leaseRecordKey(months, downPayment))?.monthlyPayment != null)
    );
};

/**
 * Program-seeded PQ populate for the LEASE grid: forces PQ, sets the axes, and seeds a lender program
 * per term. The offer-type-aware payment-grid middleware then chains PAYMENT_RECEIVED ->
 * loadPaymentGridData -> per-row PQ payments for LEASE — the same flow the main lease grid uses. Used
 * by the Tier-3 initial populate in ensureAltStructureLeaseGrid; term/down edits use targeted
 * column/row reloads instead.
 */
const populateLeaseGrid = (dispatch: AnyDispatch, termMonthsList: number[], downPaymentList: number[]): void => {
    dispatch(updateCurrentCalculationEndpoint(CALC_ENDPOINTS.programQuotes, LEASE, { withPayment: false }));
    dispatch(initializePaymentGridTerms(LEASE, termMonthsList));
    dispatch(initializePaymentGridCashdown(LEASE, downPaymentList));
    dispatch(
        addPaymentGridMeta(tagOfferType(loadPaymentGridLenderPrograms(termMonthsList, 0, LEASE), LEASE), { isMainPayment: false })
    );
};

/**
 * Ensures the shared LEASE payment-grid state (`offerDetails[lease].paymentGridData`) is populated for
 * the AI Alternative Deal Structure slideout. Dispatched on slideout open; reads/writes the same
 * offer-redux lease state the main-page lease grid uses, keeping the two in sync. Does not change the
 * deal's current offer type or trigger finance recalcs, and never force-PQs over existing lease state
 * (it may hold the user's UPQ overrides).
 *
 * Three tiers (see inline): reuse if the grid already has cells; else rehydrate the canonical axes from
 * cached records with no PS calls; else populate via PQ (program-seeded, finance-first case).
 *
 * `forceFresh` (passed when a deal change marked the grid stale) skips the reuse + cached-rehydrate
 * tiers and re-PQs the canonical axes, so the lease grid reflects the current deal — the lease counterpart
 * of the finance stale-refresh (US2000385).
 */
export const ensureAltStructureLeaseGrid =
    (forceFresh = false) =>
    (dispatch: AnyDispatch, getState: () => any): void => {
        const state = getState();

        // Tier 1 — grid view already populated (real cells or a row in flight): reuse as-is. Skipped on a
        // forced refresh (a deal/vehicle change marked the grid stale): the existing cells are the prior
        // deal's and must be re-PQ'd.
        if (!forceFresh && hasLeaseCellDataOrLoading(state)) {
            return;
        }

        // Resolve the intended axes once: existing lease grid axes if present, else deal-centered
        // defaults. This is the canonical 3x3 to display — not a union of every persisted record
        // (records from another grid context, e.g. the main grid's own down payments, would add extra
        // rows). Bails without dispatching when the deal has no usable lease config (e.g. dealer doesn't
        // offer lease) so the slideout degrades to empty cells rather than throwing.
        let termMonthsList = toNumbers(getPaymentGridTerms(state, LEASE));
        let downPaymentList = toNumbers(getPaymentGridCashDown(state, LEASE));
        if (!termMonthsList.length || !downPaymentList.length) {
            try {
                termMonthsList = toNumbers(getInitialTermsMonthsListForGrid(state, LEASE));
                downPaymentList = toNumbers(getInitialDownPaymentListForGrid(state, LEASE));
            } catch {
                return;
            }
        }
        if (!termMonthsList.length || !downPaymentList.length) {
            return;
        }

        // Forced refresh — skip the cached-records rehydrate (those carry the prior deal's/vehicle's
        // payments) and re-PQ so the grid reflects the current deal.
        if (forceFresh) {
            populateLeaseGrid(dispatch, termMonthsList, downPaymentList);
            return;
        }

        // Tier 2 — grid view gone but cached lease payments cover these axes (e.g. the main lease grid
        // was toggled off, wiping the axes but leaving the term records): rebuild just the axes and
        // re-display from Redux — no PS calls. Shows whatever lease data is in Redux for the canonical
        // 3x3, not a union of every historical record.
        if (leaseRecordsCoverRows(state, termMonthsList, downPaymentList)) {
            dispatch(initializePaymentGridTerms(LEASE, termMonthsList));
            dispatch(initializePaymentGridCashdown(LEASE, downPaymentList));
            return;
        }

        // Tier 3 — no cached lease data for these axes (genuine finance-first, lease never calculated):
        // populate via PQ.
        populateLeaseGrid(dispatch, termMonthsList, downPaymentList);
    };

/**
 * Commits a lease term-column edit from the Alt Deal Structure slideout into the shared lease grid
 * state, reloading only that column: sets the term axis, seeds a lender program for the new term, then
 * the offer-type-aware middleware fires per-row payments for that term. Other columns keep their cells.
 * Keeps the slideout and main-page lease grid in sync.
 *
 * @param key   index of the edited term column
 * @param term  new term (months) for that column
 */
export const updateAltStructureLeaseTerm =
    (key: number, term: number) =>
    (dispatch: AnyDispatch, getState: () => any): void => {
        const state = getState();
        const nextTerms = toNumbers(getPaymentGridTerms(state, LEASE).map((t, i) => (i === key ? term : t)));
        const downPaymentList = toNumbers(getPaymentGridCashDown(state, LEASE));
        if (!nextTerms.length || !downPaymentList.length) {
            return;
        }

        // Reload ONLY the edited column (not the whole grid): set the term axis, seed a lender program
        // for the new term, then defer to the offer-type-aware middleware, which chains
        // PAYMENT_RECEIVED -> loadPaymentGridTermData -> per-row payments for just that term. The other
        // columns keep their existing cells. Mirrors the main grid's single-term refresh.
        dispatch(updateCurrentCalculationEndpoint(CALC_ENDPOINTS.programQuotes, LEASE, { withPayment: false }));
        dispatch(initializePaymentGridTerms(LEASE, nextTerms));
        dispatch(
            addPaymentGridMeta(tagOfferType(loadPaymentGridTermLenderProgram(key, term, downPaymentList[0], LEASE), LEASE), {
                isMainPayment: false
            })
        );
    };

/**
 * Commits a lease down-payment (row) edit from the slideout into the shared lease grid state, reloading
 * only that row: updates the down-payment axis, then fires one PQ payment across the existing terms,
 * reusing the per-term lender programs seeded during populate. Other rows keep their cells. Keeps the
 * slideout and main-page lease grid in sync.
 *
 * @param key          index of the edited down-payment row
 * @param downPayment  new down-payment amount for that row
 */
export const updateAltStructureLeaseDownPayment =
    (key: number, downPayment: number) =>
    (dispatch: AnyDispatch, getState: () => any): void => {
        const state = getState();
        const termMonthsList = toNumbers(getPaymentGridTerms(state, LEASE));
        const nextDownPayments = toNumbers(getPaymentGridCashDown(state, LEASE).map((d, i) => (i === key ? downPayment : d)));
        if (!termMonthsList.length || !nextDownPayments.length) {
            return;
        }

        // Reload ONLY the edited row (not the whole grid): update the down-payment axis, then fire one
        // PQ payment for that row across the existing terms, reusing the per-term lender programs already
        // seeded during populate. The other rows keep their existing cells. Mirrors the main grid's
        // single-row refresh.
        dispatch(initializePaymentGridCashdown(LEASE, nextDownPayments));
        dispatch(
            addPaymentGridMeta(tagOfferType(updatePaymentGridRow(LEASE, termMonthsList, downPayment), LEASE), {
                isMainPayment: false
            })
        );
    };

/**
 * Row-level Retry Rate for the lease grid in the Alt Deal Structure slideout — the lease counterpart
 * to the finance grid's `useProgressiveFetch.retry(batchKey)` (which re-fires the whole down-payment
 * BFF batch). Re-issues the single down-payment ROW's PQ payment into the shared lease state by
 * re-dispatching `updatePaymentGridRow(LEASE, ...)` (i.e. `updateLeaseTermsGrid`, which carries
 * PAYMENT_REQUEST_OVERRIDE_NODE.lease = { termList, downPayment } + payment: true), tagged
 * offerType=LEASE so the payload targets LEASE even while the deal is on Finance. The per-term lender
 * programs seeded by the initial populate persist, so only this row flips to loading then repopulates
 * — the same single-row retry semantics as finance.
 *
 * @param downPayment  the down-payment (row) value to retry, e.g. the failed row's amount
 */
export const retryAltStructureLeaseRow =
    (downPayment: number) =>
    (dispatch: AnyDispatch, getState: () => any): void => {
        const state = getState();
        const termMonthsList = toNumbers(getPaymentGridTerms(state, LEASE));
        if (!termMonthsList.length) {
            return;
        }

        dispatch(
            addPaymentGridMeta(tagOfferType(updatePaymentGridRow(LEASE, termMonthsList, downPayment), LEASE), {
                isMainPayment: false
            })
        );
    };
